import React, { useEffect, useState } from 'react';
import { Phone, Video, PhoneOff, X } from 'lucide-react';

interface IncomingCallModalProps {
  callerId: number;
  callerName: string;
  callerAvatar?: string;
  callType: 'video' | 'voice';
  onAccept: () => void;
  onReject: () => void;
  isVisible: boolean;
}

export const IncomingCallModal: React.FC<IncomingCallModalProps> = ({
  callerId,
  callerName,
  callerAvatar,
  callType,
  onAccept,
  onReject,
  isVisible
}) => {
  const [ringDuration, setRingDuration] = useState(0);

  useEffect(() => {
    if (!isVisible) {
      setRingDuration(0);
      return;
    }

    const interval = setInterval(() => {
      setRingDuration(prev => prev + 1);
    }, 1000);

    // Auto-reject after 30 seconds
    const timeout = setTimeout(() => {
      onReject();
    }, 30000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [isVisible, onReject]);

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[100] animate-fade-in">
      <div className="bg-gradient-to-br from-gray-800 via-blue-900 to-gray-800 rounded-2xl shadow-2xl max-w-md w-full mx-4 p-8 animate-scale-in">
        {/* Close button */}
        <button
          onClick={onReject}
          className="absolute top-4 right-4 p-2 hover:bg-white/10 rounded-full transition-colors"
        >
          <X className="w-5 h-5 text-white" />
        </button>

        {/* Caller Info */}
        <div className="text-center mb-8">
          <div className="relative inline-block mb-4">
            <img
              src={callerAvatar || '/default-avatar.png'}
              alt={callerName}
              className="w-32 h-32 rounded-full ring-4 ring-blue-500 animate-pulse-ring"
            />
            {callType === 'video' ? (
              <div className="absolute -bottom-2 -right-2 p-3 bg-blue-600 rounded-full">
                <Video className="w-6 h-6 text-white" />
              </div>
            ) : (
              <div className="absolute -bottom-2 -right-2 p-3 bg-green-600 rounded-full">
                <Phone className="w-6 h-6 text-white" />
              </div>
            )}
          </div>

          <h2 className="text-2xl font-bold text-white mb-2">
            {callerName}
          </h2>
          <p className="text-gray-300 text-lg mb-1">
            {callType === 'video' ? 'Cuộc gọi video' : 'Cuộc gọi thoại'}
          </p>
          <p className="text-gray-400 text-sm">
            Đang đổ chuông... {ringDuration}s
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-center gap-6">
          {/* Reject Button */}
          <button
            onClick={onReject}
            className="group flex flex-col items-center gap-2"
          >
            <div className="p-4 bg-red-600 hover:bg-red-700 rounded-full shadow-lg group-hover:scale-110 transition-all">
              <PhoneOff className="w-8 h-8 text-white" />
            </div>
            <span className="text-white text-sm font-medium">Từ chối</span>
          </button>

          {/* Accept Button */}
          <button
            onClick={onAccept}
            className="group flex flex-col items-center gap-2"
          >
            <div className="p-4 bg-green-600 hover:bg-green-700 rounded-full shadow-lg group-hover:scale-110 transition-all animate-pulse">
              {callType === 'video' ? (
                <Video className="w-8 h-8 text-white" />
              ) : (
                <Phone className="w-8 h-8 text-white" />
              )}
            </div>
            <span className="text-white text-sm font-medium">Trả lời</span>
          </button>
        </div>

        {/* Ringtone Indicator */}
        <div className="mt-6 flex items-center justify-center gap-2">
          <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
          <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
          <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
      </div>
    </div>
  );
};

// Mini notification for incoming call (can be used in notification panel)
interface CallNotificationProps {
  callerId: number;
  callerName: string;
  callerAvatar?: string;
  callType: 'video' | 'voice';
  onAccept: () => void;
  onReject: () => void;
}

export const CallNotification: React.FC<CallNotificationProps> = ({
  callerId,
  callerName,
  callerAvatar,
  callType,
  onAccept,
  onReject
}) => {
  return (
    <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg shadow-xl p-4 animate-slide-in-right">
      <div className="flex items-center gap-3 mb-3">
        <img
          src={callerAvatar || '/default-avatar.png'}
          alt={callerName}
          className="w-12 h-12 rounded-full ring-2 ring-white"
        />
        <div className="flex-1">
          <h3 className="text-white font-semibold">{callerName}</h3>
          <p className="text-white/80 text-sm">
            {callType === 'video' ? 'Cuộc gọi video' : 'Cuộc gọi thoại'}
          </p>
        </div>
        {callType === 'video' ? (
          <Video className="w-5 h-5 text-white" />
        ) : (
          <Phone className="w-5 h-5 text-white" />
        )}
      </div>

      <div className="flex gap-2">
        <button
          onClick={onReject}
          className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
        >
          <PhoneOff className="w-4 h-4" />
          Từ chối
        </button>
        <button
          onClick={onAccept}
          className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
        >
          {callType === 'video' ? (
            <Video className="w-4 h-4" />
          ) : (
            <Phone className="w-4 h-4" />
          )}
          Trả lời
        </button>
      </div>
    </div>
  );
};
